import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, BarChart3, Award, Users, Clock, Filter } from 'lucide-react';
import LeaderboardFilters from './LeaderboardFilters';
import leaderboardService from '../../services/leaderboardService';

const AccuracyLeaderboard = ({ filters, onFilterChange }) => {
  const [accuracyData, setAccuracyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userStats, setUserStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const userId = JSON.parse(localStorage.getItem("usuarioConCedula") || "{}").id;

  useEffect(() => {
    loadAccuracyData();
    loadUserStats();
    loadAnalytics();
  }, [filters, currentPage]);

  const loadAccuracyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the average-score leaderboard endpoint from the Postman collection
      const data = await leaderboardService.getAccuracyLeaderboardData({
        ...filters,
        top: 50
      });
      
      setAccuracyData(data || []);
      setTotalPages(Math.ceil((data?.length || 0) / 20));
    } catch (error) {
      console.error('Error loading accuracy data:', error);
      setError('Error al cargar los datos de precisión');
    } finally {
      setLoading(false);
    }
  };

  const loadUserStats = async () => {
    if (!userId) return;

    try {
      // Use the user performance summary endpoint
      const stats = await leaderboardService.getUserLeaderboardStats(userId);
      setUserStats(stats);
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      // Use global statistics for analytics
      const analyticsData = await leaderboardService.getGlobalStatistics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 90) return 'text-green-600';
    if (accuracy >= 80) return 'text-blue-600';
    if (accuracy >= 70) return 'text-yellow-600';
    if (accuracy >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getAccuracyBadge = (accuracy) => {
    if (accuracy >= 95) return { text: 'Excelente', color: 'bg-green-100 text-green-800' };
    if (accuracy >= 85) return { text: 'Muy Bueno', color: 'bg-blue-100 text-blue-800' };
    if (accuracy >= 75) return { text: 'Bueno', color: 'bg-yellow-100 text-yellow-800' };
    if (accuracy >= 65) return { text: 'Regular', color: 'bg-orange-100 text-orange-800' };
    return { text: 'Necesita Mejorar', color: 'bg-red-100 text-red-800' };
  };

  const formatAccuracy = (accuracy) => {
    return `${accuracy.toFixed(1)}%`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Precisión General
            </h2>
            <p className="text-gray-600">
              Comparación de precisión promedio en todos los cuestionarios
            </p>
          </div>
          {userStats && (
            <div className="bg-green-aqua/10 border border-green-aqua/20 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-aqua" />
                <span className="text-sm font-medium text-green-aqua">
                  Tu precisión: {formatAccuracy(userStats.averageAccuracy || userStats.averageScore || 0)}
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Posición: #{userStats.position || 'N/A'} de {userStats.totalUsers || 'N/A'}
              </div>
            </div>
          )}
        </div>

        {/* Analytics Summary */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">Usuarios Activos</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">{analytics.totalUsers || 0}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-600">Precisión Promedio</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{formatAccuracy(analytics.averageAccuracy || 0)}</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-600">Mejor Precisión</span>
              </div>
              <div className="text-2xl font-bold text-yellow-900">{formatAccuracy(analytics.highestAccuracy || 0)}</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-600">Cuestionarios Completados</span>
              </div>
              <div className="text-2xl font-bold text-purple-900">{analytics.totalQuestionnaires || 0}</div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <LeaderboardFilters
        filters={filters}
        onFilterChange={onFilterChange}
        showAccuracyFilters={true}
      />

      {/* Accuracy Leaderboard Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full border-4 border-green-aqua/20 border-t-green-aqua animate-spin"></div>
            <p className="text-gray-600">Cargando clasificación de precisión...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-2xl">!</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadAccuracyData}
              className="px-4 py-2 bg-green-aqua text-white rounded-lg hover:bg-green-aqua/90 transition-colors"
            >
              Reintentar
            </button>
          </div>
        ) : accuracyData.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <Target className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos</h3>
            <p className="text-gray-600">Aún no hay datos de precisión disponibles</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Posición
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precisión Promedio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cuestionarios Completados
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mejor Puntuación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Última Actividad
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accuracyData.map((entry, index) => {
                  const position = (currentPage - 1) * 20 + index + 1;
                  const accuracy = entry.averageAccuracy || entry.averageScore || 0;
                  const accuracyBadge = getAccuracyBadge(accuracy);
                  const completedQuestionnaires = entry.completedQuestionnaires || 0;
                  const totalQuestionnaires = entry.totalQuestionnaires || 1;
                  const bestScore = entry.bestScore || 0;
                  const bestQuestionnaire = entry.bestQuestionnaire || 'N/A';
                  const lastActivity = entry.lastActivity;
                  
                  return (
                    <tr
                      key={entry.userId}
                      className={`hover:bg-gray-50 transition-colors ${
                        entry.userId === parseInt(userId) ? 'bg-green-aqua/5' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            position === 1 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white' :
                            position === 2 ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-white' :
                            position === 3 ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white' :
                            'bg-white text-gray-900 border border-gray-200'
                          }`}>
                            {position}
                          </div>
                          {position <= 3 && (
                            <Award className={`w-5 h-5 ${
                              position === 1 ? 'text-yellow-500' :
                              position === 2 ? 'text-gray-400' :
                              'text-amber-600'
                            }`} />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img
                              className="h-10 w-10 rounded-full"
                              src={entry.userAvatar || "/img/user.png"}
                              alt={entry.userName}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {entry.userName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {entry.userEmail}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`text-lg font-bold ${getAccuracyColor(accuracy)}`}>
                            {formatAccuracy(accuracy)}
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${accuracyBadge.color}`}>
                            {accuracyBadge.text}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{completedQuestionnaires}</span>
                          <span className="text-gray-500">/ {totalQuestionnaires}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-green-aqua h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(completedQuestionnaires / totalQuestionnaires) * 100}%` }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{formatAccuracy(bestScore)}</span>
                          <span className="text-xs text-gray-500">en {bestQuestionnaire}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lastActivity ? formatDate(lastActivity) : 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccuracyLeaderboard; 